import React, { useCallback, useEffect, useState } from 'react';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2, X } from 'lucide-react';
import { Location, Position, WorkingTimeRestRule } from '../types';
import { locationApi, positionApi, workingTimeRestRulesApi } from '../services/api';
import { appConfirm } from '../context/SystemDialogContext';

type Props = {
  locations: Location[];
  positions: Position[];
  toast: { success: (m: string) => void; error: (m: string) => void };
  onMastersLoaded?: (locations: Location[], positions: Position[]) => void;
};

/** null = Any Location / Any Position */
const emptyForm = {
  locationId: null as number | null,
  positionId: null as number | null,
  minRestBetweenShiftsHours: 11,
  minWeeklyRestHours: 24,
  maxAverageWeeklyHours: 48,
  referencePeriodWeeks: 17,
  isActive: true,
};

export const WorkingTimeRestRulesPanel: React.FC<Props> = ({
  locations: locationsProp,
  positions: positionsProp,
  toast,
  onMastersLoaded,
}) => {
  const [rows, setRows] = useState<WorkingTimeRestRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [movingId, setMovingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [locations, setLocations] = useState<Location[]>(locationsProp);
  const [positions, setPositions] = useState<Position[]>(positionsProp);

  useEffect(() => {
    setLocations(locationsProp);
  }, [locationsProp]);

  useEffect(() => {
    setPositions(positionsProp);
  }, [positionsProp]);

  const enrichRows = useCallback(
    (data: WorkingTimeRestRule[], locs: Location[], poss: Position[]) => {
      const locMap = new Map(locs.map((l) => [l.id, l.name]));
      const posMap = new Map(poss.map((p) => [p.id, p.title]));
      return data.map((r, i) => ({
        ...r,
        ruleNo: r.ruleNo ?? i + 1,
        locationName:
          r.locationId == null
            ? 'Any Location'
            : r.locationName || locMap.get(r.locationId) || String(r.locationId),
        positionTitle:
          r.positionId == null
            ? 'Any Position'
            : r.positionTitle || posMap.get(r.positionId) || String(r.positionId),
      }));
    },
    []
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await workingTimeRestRulesApi.list();
      let locs = locationsProp;
      let poss = positionsProp;
      if (locs.length === 0 || poss.length === 0) {
        const [l, p] = await Promise.all([
          locs.length === 0 ? locationApi.getAll().catch(() => []) : Promise.resolve(locs),
          poss.length === 0 ? positionApi.getAll().catch(() => []) : Promise.resolve(poss),
        ]);
        locs = l || [];
        poss = p || [];
        if (locationsProp.length === 0) setLocations(locs);
        if (positionsProp.length === 0) setPositions(poss);
        onMastersLoaded?.(locs, poss);
      }
      setRows(enrichRows(Array.isArray(data) ? data : [], locs, poss));
    } catch (e: any) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message;
      const msg =
        e?.code === 'ECONNABORTED'
          ? 'Timed out talking to the API. Restart Staffly.API, then click Retry.'
          : detail || e?.message || 'Failed to load rest rules';
      setLoadError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeModal = () => {
    setModalOpen(false);
    setForm(emptyForm);
    setEditingId(null);
  };

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row: WorkingTimeRestRule) => {
    setEditingId(Number(row.id));
    setForm({
      locationId: row.locationId == null ? null : Number(row.locationId),
      positionId: row.positionId == null ? null : Number(row.positionId),
      minRestBetweenShiftsHours: Number(row.minRestBetweenShiftsHours),
      minWeeklyRestHours: Number(row.minWeeklyRestHours),
      maxAverageWeeklyHours: Number(row.maxAverageWeeklyHours),
      referencePeriodWeeks: Number(row.referencePeriodWeeks) || 17,
      isActive: row.isActive !== false,
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        locationId: form.locationId,
        positionId: form.positionId,
        minRestBetweenShiftsHours: form.minRestBetweenShiftsHours,
        minWeeklyRestHours: form.minWeeklyRestHours,
        maxAverageWeeklyHours: form.maxAverageWeeklyHours,
        referencePeriodWeeks: form.referencePeriodWeeks,
        isActive: form.isActive,
      };
      if (editingId != null) {
        await workingTimeRestRulesApi.update(editingId, payload);
        toast.success('Rest rule updated');
      } else {
        await workingTimeRestRulesApi.create(payload);
        toast.success('Rest rule created');
      }
      closeModal();
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to save rest rule');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!(await appConfirm('Delete this rest rule?', { danger: true, confirmLabel: 'Delete' }))) return;
    try {
      await workingTimeRestRulesApi.remove(id);
      toast.success('Rest rule deleted');
      if (editingId === id) closeModal();
      await load();
    } catch {
      toast.error('Failed to delete rest rule');
    }
  };

  const handleMove = async (id: number, direction: 'up' | 'down') => {
    setMovingId(id);
    try {
      await workingTimeRestRulesApi.move(id, direction);
      await load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to reorder rule');
    } finally {
      setMovingId(null);
    }
  };

  const locationOptions = React.useMemo(() => {
    const active = locations.filter((l) => l.isActive !== false);
    if (form.locationId == null) return active;
    if (active.some((l) => l.id === form.locationId)) return active;
    const selected = locations.find((l) => l.id === form.locationId);
    return selected ? [selected, ...active] : active;
  }, [locations, form.locationId]);

  const positionOptions = React.useMemo(() => {
    const active = positions.filter((p) => p.isActive !== false);
    if (form.positionId == null) return active;
    if (active.some((p) => p.id === form.positionId)) return active;
    const selected = positions.find((p) => p.id === form.positionId);
    return selected ? [selected, ...active] : active;
  }, [positions, form.positionId]);

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold">Working Time / Rest Rules</h2>
          <p className="text-sm text-neutral-600 mt-1 max-w-3xl">
            Rules are evaluated top to bottom (Rule No. 1 first). The first rule whose location and
            position match the shift is used; if criteria do not match, the next rule is tried.
            Use Any Location / Any Position for catch-all rows. Soft warnings only — defaults 11h /
            24h / 48h over 17 weeks if nothing matches.
          </p>
        </div>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 shrink-0"
        >
          <Plus className="h-4 w-4" />
          Add rule
        </button>
      </div>

      <div className="bg-white rounded-lg border border-neutral-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-neutral-500">Loading rules…</div>
        ) : loadError ? (
          <div className="p-8 text-center space-y-3">
            <p className="text-sm text-red-700">{loadError}</p>
            <button
              type="button"
              onClick={() => void load()}
              className="px-3 py-1.5 rounded-md border border-neutral-300 text-sm hover:bg-neutral-50"
            >
              Retry
            </button>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-neutral-500 text-sm">
            No rules yet. Click Add rule to create one, or rely on defaults (11 / 24 / 48).
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-neutral-50 text-left text-neutral-600">
                <tr>
                  <th className="px-3 py-2 font-medium w-16">Rule No.</th>
                  <th className="px-3 py-2 font-medium w-24">Order</th>
                  <th className="px-3 py-2 font-medium">Location</th>
                  <th className="px-3 py-2 font-medium">Position</th>
                  <th className="px-3 py-2 font-medium">Between</th>
                  <th className="px-3 py-2 font-medium">Weekly rest</th>
                  <th className="px-3 py-2 font-medium">Max avg</th>
                  <th className="px-3 py-2 font-medium">Period</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                  <th className="px-3 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {rows.map((r, index) => (
                  <tr key={r.id} className="border-t border-neutral-100">
                    <td className="px-3 py-2 font-medium text-neutral-800">{r.ruleNo ?? index + 1}</td>
                    <td className="px-3 py-2">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          type="button"
                          title="Move up (higher priority)"
                          disabled={index === 0 || movingId === r.id}
                          onClick={() => void handleMove(r.id, 'up')}
                          className="p-1 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-30"
                        >
                          <ArrowUp className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          title="Move down (lower priority)"
                          disabled={index === rows.length - 1 || movingId === r.id}
                          onClick={() => void handleMove(r.id, 'down')}
                          className="p-1 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-30"
                        >
                          <ArrowDown className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                    <td className="px-3 py-2">{r.locationName}</td>
                    <td className="px-3 py-2">{r.positionTitle}</td>
                    <td className="px-3 py-2">{r.minRestBetweenShiftsHours}h</td>
                    <td className="px-3 py-2">{r.minWeeklyRestHours}h</td>
                    <td className="px-3 py-2">{r.maxAverageWeeklyHours}h</td>
                    <td className="px-3 py-2">{r.referencePeriodWeeks}w</td>
                    <td className="px-3 py-2">{r.isActive ? 'Active' : 'Inactive'}</td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button
                        type="button"
                        className="text-blue-700 hover:underline mr-3 inline-flex items-center gap-1"
                        onClick={() => openEdit(r)}
                      >
                        <Pencil className="h-3.5 w-3.5" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className="text-red-700 hover:underline inline-flex items-center gap-1"
                        onClick={() => void handleDelete(r.id)}
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
          role="dialog"
          aria-modal="true"
          aria-labelledby="rest-rule-modal-title"
          onClick={(e) => {
            if (e.target === e.currentTarget) closeModal();
          }}
        >
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200">
              <h3 id="rest-rule-modal-title" className="text-base font-semibold text-neutral-900">
                {editingId != null ? 'Edit rest rule' : 'Add rest rule'}
              </h3>
              <button
                type="button"
                onClick={closeModal}
                className="p-1 rounded hover:bg-neutral-100 text-neutral-500"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm">
                  <span className="text-neutral-600">Location</span>
                  <select
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.locationId == null ? '' : String(form.locationId)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        locationId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  >
                    <option value="">Any Location</option>
                    {locationOptions.map((l) => (
                      <option key={l.id} value={String(l.id)}>
                        {l.name}
                        {l.isActive === false ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-600">Position</span>
                  <select
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.positionId == null ? '' : String(form.positionId)}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        positionId: e.target.value ? Number(e.target.value) : null,
                      }))
                    }
                  >
                    <option value="">Any Position</option>
                    {positionOptions.map((p) => (
                      <option key={p.id} value={String(p.id)}>
                        {p.title}
                        {p.isActive === false ? ' (inactive)' : ''}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-600">Between shifts (hours)</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.minRestBetweenShiftsHours}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        minRestBetweenShiftsHours: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-600">Weekly rest (hours)</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.minWeeklyRestHours}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        minWeeklyRestHours: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-600">Max average weekly hours</span>
                  <input
                    type="number"
                    min={0}
                    step={0.5}
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.maxAverageWeeklyHours}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        maxAverageWeeklyHours: parseFloat(e.target.value) || 0,
                      }))
                    }
                  />
                </label>
                <label className="block text-sm">
                  <span className="text-neutral-600">Reference period (weeks)</span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className="mt-1 w-full border border-neutral-300 rounded-md px-3 py-2 text-sm"
                    value={form.referencePeriodWeeks}
                    onChange={(e) =>
                      setForm((f) => ({
                        ...f,
                        referencePeriodWeeks: parseInt(e.target.value, 10) || 17,
                      }))
                    }
                  />
                </label>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-neutral-700">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="flex justify-end gap-2 px-4 py-3 border-t border-neutral-200 bg-neutral-50">
              <button
                type="button"
                onClick={closeModal}
                className="px-3 py-2 rounded-md border border-neutral-300 text-sm hover:bg-white"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={() => void handleSave()}
                className="px-3 py-2 rounded-md bg-blue-600 text-white text-sm hover:bg-blue-700 disabled:opacity-50"
              >
                {saving ? 'Saving…' : editingId != null ? 'Save changes' : 'Create rule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
